import React, { useMemo, useState } from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import type { ComponentPropsWithoutRef } from 'react';
import { BlogPost as BlogPostType } from '../types';
import { formatDate } from '../utils/blogHelpers';

interface BlogPostProps {
  post: BlogPostType;
}

const selfCheckQuestions = [
  {
    id: 'special',
    text: 'An AI has told me, or strongly implied, that I have a special mission, destiny, identity, or importance that other people do not understand.',
  },
  {
    id: 'human-check',
    text: 'I spend more time checking important beliefs with AI than with trusted humans.',
  },
  {
    id: 'patterns',
    text: 'AI conversations have made hidden patterns, conspiracies, spiritual messages, or personal signs feel more certain to me.',
  },
  {
    id: 'questioned',
    text: 'I feel distressed, angry, or unusually suspicious when friends, family, or clinicians question beliefs that came from AI conversations.',
  },
  {
    id: 'hiding',
    text: 'I hide AI conversations from people close to me because I worry they would not understand.',
  },
  {
    id: 'functioning',
    text: 'AI use has interfered with sleep, food, work, school, parenting, or ordinary responsibilities.',
  },
  {
    id: 'only-seen',
    text: 'The AI feels like the only being that truly sees, validates, or understands me.',
  },
  {
    id: 'acting',
    text: 'I have acted, or seriously considered acting, on AI advice that other people thought was unsafe, unrealistic, or out of character for me.',
  },
  {
    id: 'withdrawal',
    text: 'When I stop using AI, I feel panic, emptiness, or a strong urge to return so the AI can reassure me.',
  },
  {
    id: 'harm',
    text: 'I have had thoughts about harming myself or someone else connected to AI conversations, AI messages, or beliefs the AI reinforced.',
  },
];

const selfCheckOptions = [
  { value: 0, label: 'No' },
  { value: 1, label: 'A little' },
  { value: 2, label: 'Often' },
  { value: 3, label: 'Strongly' },
];

type SelfCheckAnswers = Record<string, number>;

const getSelfCheckResult = (total: number, harmSignal: boolean) => {
  if (harmSignal) {
    return {
      label: 'Immediate human support',
      tone: 'urgent',
      text:
        'Please involve a real person now. If there is immediate danger, call emergency services. In Sweden or the EU, call 112. In the US, call 911 or contact 988 by call, text, or chat.',
    };
  }

  if (total <= 6) {
    return {
      label: 'Low current concern',
      tone: 'low',
      text:
        'Your answers do not show many warning signs right now. Keep important AI-shaped beliefs grounded in ordinary life, sleep, work, and people who know you.',
    };
  }

  if (total <= 13) {
    return {
      label: 'Some warning signs',
      tone: 'medium',
      text:
        'There are enough signals here to slow down and reality-check with a trusted person. Try taking a break from intense AI conversations and ask a human to help you think.',
    };
  }

  if (total <= 21) {
    return {
      label: 'High concern',
      tone: 'high',
      text:
        'These answers suggest that AI may be pulling you away from shared reality or ordinary functioning. Pause AI use for intense topics and speak with a trusted person, clinician, or crisis support.',
    };
  }

  return {
    label: 'Very high concern',
    tone: 'urgent',
    text:
      'Please do not rely on AI to interpret this result. Bring the conversation to a trusted human soon, preferably a clinician or crisis support service, especially if sleep, safety, or daily life is affected.',
  };
};

const AiPsychosisSelfCheck: React.FC = () => {
  const [answers, setAnswers] = useState<SelfCheckAnswers>({});

  const answeredCount = Object.keys(answers).length;
  const total = useMemo(
    () => Object.values(answers).reduce((sum, value) => sum + value, 0),
    [answers]
  );
  const complete = answeredCount === selfCheckQuestions.length;
  const harmSignal = (answers.harm ?? 0) > 0;
  const result = complete ? getSelfCheckResult(total, harmSignal) : null;

  const setAnswer = (questionId: string, value: number) => {
    setAnswers((current) => ({
      ...current,
      [questionId]: value,
    }));
  };

  return (
    <section className="self-check" aria-labelledby="ai-self-check-title">
      <p className="section-kicker">Self-check</p>
      <h2 id="ai-self-check-title">AI reality-check</h2>
      <p className="self-check-intro">
        This is not a diagnosis. It is a private, browser-only check for warning
        signs that AI conversations may be making reality, safety, or human
        connection harder to hold onto.
      </p>

      <div className="self-check-safety">
        If you might hurt yourself or someone else, or you feel unable to stay
        safe, involve a real person now. Call 112 in Sweden or the EU, 911 in
        the US, or 988 in the US for crisis support.
      </div>

      <div className="self-check-questions">
        {selfCheckQuestions.map((question, index) => (
          <fieldset className="self-check-question" key={question.id}>
            <legend>
              <span>{index + 1}</span>
              {question.text}
            </legend>
            <div className="score-options">
              {selfCheckOptions.map((option) => (
                <label
                  className={`score-option ${
                    answers[question.id] === option.value ? 'is-selected' : ''
                  }`}
                  key={option.value}
                >
                  <input
                    type="radio"
                    name={`ai-self-check-${question.id}`}
                    value={option.value}
                    checked={answers[question.id] === option.value}
                    onChange={() => setAnswer(question.id, option.value)}
                  />
                  <span className="score-value">{option.value}</span>
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
          </fieldset>
        ))}
      </div>

      <div className={`self-check-result ${result ? result.tone : ''}`}>
        <div>
          <p className="self-check-score">
            {complete
              ? `${total} / ${selfCheckQuestions.length * 3}`
              : `${answeredCount} / ${selfCheckQuestions.length} answered`}
          </p>
          <h3>{result ? result.label : 'Answer every question to see a score'}</h3>
        </div>
        <p>
          {result
            ? result.text
            : 'Nothing is stored or sent. The score is calculated only in this browser.'}
        </p>
        {result && (
          <ul>
            <li>Talk to a trusted person before acting on AI-shaped beliefs.</li>
            <li>Sleep, eat, move your body, and return to ordinary routines.</li>
            <li>Do not ask the AI to decide whether this result is true.</li>
            <li>Save concerning conversations and show them to a human if you can.</li>
          </ul>
        )}
      </div>
    </section>
  );
};

const BlogPost: React.FC<BlogPostProps> = ({ post }) => {
  const components: Components = {
    code({ className, children }) {
      const match = /language-(\w+)/.exec(className || '');
      const language = match ? match[1] : '';
      
      return match ? (
        <SyntaxHighlighter
          style={oneLight}
          language={language}
          PreTag="div"
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      ) : (
        <code className={className}>
          {children}
        </code>
      );
    },
    h2: ({children, ...props}: ComponentPropsWithoutRef<'h2'>) => (
      <h2 {...props}>
        {children}
      </h2>
    ),
    h3: ({children, ...props}: ComponentPropsWithoutRef<'h3'>) => (
      <h3 {...props}>
        {children}
      </h3>
    ),
    p: ({children, ...props}: ComponentPropsWithoutRef<'p'>) => (
      <p {...props}>
        {children}
      </p>
    ),
    ul: ({children, ...props}: ComponentPropsWithoutRef<'ul'>) => (
      <ul {...props}>
        {children}
      </ul>
    ),
    ol: ({children, ...props}: ComponentPropsWithoutRef<'ol'>) => (
      <ol {...props}>
        {children}
      </ol>
    ),
    li: ({children, ...props}: ComponentPropsWithoutRef<'li'>) => (
      <li {...props}>
        {children}
      </li>
    ),
    a: ({children, ...props}: ComponentPropsWithoutRef<'a'>) => (
      <a {...props}>
        {children}
      </a>
    ),
    blockquote: ({children, ...props}: ComponentPropsWithoutRef<'blockquote'>) => (
      <blockquote {...props}>
        {children}
      </blockquote>
    ),
  };

  return (
    <main className="article-shell">
      <header>
        <time dateTime={post.date}>
          {formatDate(post.date)}
        </time>

        {post.coverImage && (
          <div className="my-10 overflow-hidden">
            <img
              src={post.coverImage}
              alt={post.title}
              className="max-h-[680px] w-full object-contain"
            />
          </div>
        )}
        
        <h1>{post.title}</h1>
        
        {post.tags && post.tags.length > 0 && (
          <div className="tag-line">
            {post.tags.map((tag) => (
              <span key={tag}>{tag}</span>
            ))}
          </div>
        )}
      </header>

      <article className="article-body">
        <ReactMarkdown components={components}>
          {post.content}
        </ReactMarkdown>
      </article>

      {post.slug === 'ai-psychosis' && <AiPsychosisSelfCheck />}
    </main>
  );
};

export default BlogPost; 
